'use client';

import React, { ReactNode } from 'react';
import dynamic from 'next/dynamic';
import {
  FaLocationDot,
  FaEnvelope,
  FaPhone,
  FaTruckMedical,
  FaFireExtinguisher,
  FaShieldCat,
  FaHeadset,
} from 'react-icons/fa6';

const ContactMap = dynamic(() => import('./ContactMap'), { ssr: false });

const ICON_MAP: Record<string, React.ElementType> = {
  postal: FaLocationDot,
  email: FaEnvelope,
  tel: FaPhone,
  ambulance: FaTruckMedical,
  fire: FaFireExtinguisher,
  security: FaShieldCat,
  support: FaHeadset,
};

interface ContactDetail {
  id: string;
  label: string;
  value: ReactNode;
}

type ContactInfoProps = {
  cfg: {
    name: string;
    contact?: {
      title?: string;
      details?: ContactDetail[];
      hq?: { coords?: [number, number]; label?: string };
      map?: { zoom?: number; scrollWheelZoom?: boolean; tileUrl?: string };
      mapIcon?: string;
    };
  };
};

export default function ContactInfo({ cfg }: ContactInfoProps) {
  const details = (cfg.contact?.details ?? []) as ContactDetail[];
  const title = cfg.contact?.title ?? 'Contact Details';

  return (
    <div className="flex flex-col bg-white border border-[#4d6b57] rounded-md p-6 md:p-8 shadow-sm">
      <div className="w-full">
        <div className="h-56 sm:h-64 md:h-72 lg:h-[250px] overflow-hidden rounded">
          <ContactMap cfg={{ name: cfg.name, contact: cfg.contact }} />
        </div>
      </div>

      <div className="mt-6">
        <h2 className="text-lg font-semibold text-[#253C2D] mb-4">{title}</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {details.map(({ id, label, value }) => {
            const Icon = ICON_MAP[id] || FaLocationDot;
            return (
              <div key={id} className="flex items-start gap-3">
                <Icon className="text-[#253C2D] mt-1" />
                <div className="text-[#253C2D]">
                  <p className="text-sm">{label}:</p>
                  <p className="font-bold leading-snug">{value}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
